import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Popper,
  Stack,
  styled,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Autocomplete } from "src/components";
import { useSession } from "src/hooks";
import { ActionItemDetailDto, useEditActionItemSection, useGetPlanStructure, useGetTeams } from "src/http";
import { ITeamSummary } from "src/models";

const MenuItem = styled(Box)`
  &:first-child {
    margin-top: ${({ theme }) => theme.spacing(0.5)};
  }

  &:last-child {
    margin-bottom: 2px;
  }
` as typeof Box;

interface IProps {
  open: boolean;
  actionItem: ActionItemDetailDto;
  onUpdated: (selectedTeam: ITeamSummary | null, selectedSection: ITeamSection | null) => void;
  onCancelled: () => void;
}

interface ITeamSection {
  blockName: string;
  sectionName: string;
  sectionGuid: string;
}

const EditActionItemSectionModal = (props: IProps) => {
  const { userId } = useSession();
  const [selectedTeam, setSelectedTeam] = useState<ITeamSummary | null>(null);
  const [selectedSection, setSelectedSection] = useState<ITeamSection | null>(null);
  const [sections, setSections] = useState<ITeamSection[]>([]);
  const { data: teams, isFetching: isFetchingTeams } = useGetTeams(userId, props.open);
  const { data: planStructure, isFetching: isFetchingStructure } = useGetPlanStructure(
    selectedTeam?.planGuid!,
    props.open
  );
  const { mutate: editActionItemSection, isLoading: isUpdatingSection } = useEditActionItemSection();
  const theme = useTheme();

  useEffect(() => {
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.actionItem]);

  useEffect(() => {
    setSections(
      planStructure?.blocks.flatMap((block) =>
        block.sections.map(
          (section): ITeamSection => ({
            blockName: block.name,
            sectionName: section.name,
            sectionGuid: section.guid,
          })
        )
      ) ?? []
    );
  }, [planStructure]);

  async function handlePlanChanged(e: React.SyntheticEvent, value: ITeamSummary | null) {
    setSelectedTeam(value);
    setSelectedSection(null);
  }

  function handleCancelClicked() {
    reset();
    props.onCancelled();
  }

  function reset() {
    if (props.actionItem.teamPlanSection) {
      setSelectedTeam(teams?.find((x) => x.slug === props.actionItem.teamPlanSection.teamSlug) ?? null);
      setSelectedSection({
        blockName: "",
        sectionGuid: props.actionItem.teamPlanSection.sectionGuid,
        sectionName: props.actionItem.teamPlanSection.sectionName,
      });
    } else {
      setSelectedTeam(null);
      setSelectedSection(null);
    }
  }

  async function handleDoneClicked() {
    if (selectedTeam?.planGuid == null || selectedSection == null) {
      return;
    }

    editActionItemSection(
      {
        actionItemGuid: props.actionItem.guid,
        fromPlanGuid: props.actionItem.teamPlanSection.planGuid,
        fromSectionGuid: props.actionItem.teamPlanSection.sectionGuid,
        toPlanGuid: selectedTeam.planGuid,
        toSectionGuid: selectedSection.sectionGuid,
      },
      {
        onSuccess: () => props.onUpdated(selectedTeam, selectedSection),
      }
    );
  }

  function sortFunction(a: ITeamSummary, b: ITeamSummary) {
    let aMembership = a.isAdministrator || a.isMember;
    let bMembership = b.isAdministrator || b.isMember;

    if (aMembership !== bMembership) {
      return aMembership ? -1 : 1;
    }
    return a.name.localeCompare(b.name);
  }

  return (
    <Dialog open={props.open} onClose={handleCancelClicked}>
      <DialogTitle>Change Team</DialogTitle>
      <DialogContent>
        <Stack spacing={1}>
          <Typography>Move this item to another team or section.</Typography>
          <Autocomplete
            isLoading={isFetchingTeams || !teams}
            groupBy={(option) => (option.isMember || option.isAdministrator ? "My Teams" : "Other Teams")}
            options={teams ? [...teams].sort(sortFunction) : []}
            getOptionLabel={(option: ITeamSummary) => option.name}
            renderGroup={(group) => (
              <Box key={group.key} sx={{ pb: 0.25 }}>
                <Stack
                  spacing={0.2}
                  sx={{ px: 0.5, pt: 0.75, position: "sticky", top: 0, backgroundColor: theme.palette.common.white }}
                >
                  <Typography variant="caption">{group.group.toLocaleUpperCase()}</Typography>
                  <Divider />
                </Stack>
                {group.children}
              </Box>
            )}
            renderOption={(props, option) => (
              <MenuItem {...props} key={option.slug} component="li">
                <Stack>
                  <Typography>{option.name}</Typography>
                  {option.planGuid === null && (
                    <Typography variant="caption">This team has no strategic map</Typography>
                  )}
                  {option.planGuid != null && !(option.isAdministrator || option.isMember) && (
                    <Typography variant="caption">You are not a member or administrator of this team</Typography>
                  )}
                </Stack>
              </MenuItem>
            )}
            getOptionDisabled={(option) => option.planGuid === null || !(option.isMember || option.isAdministrator)}
            isOptionEqualToValue={(option, value) => option.planGuid === value.planGuid}
            renderInput={(params) => (
              <TextField {...params} placeholder="Select a team" inputProps={{ ...params.inputProps }} />
            )}
            onChange={handlePlanChanged}
            PopperComponent={(props) => (
              <Popper {...props} style={{ ...props.style, margin: 0, padding: 0, minWidth: "300px" }} />
            )}
            value={selectedTeam}
          />

          <Autocomplete
            isLoading={selectedTeam !== null && (isFetchingStructure || !planStructure)}
            disabled={selectedTeam == null || isFetchingStructure || !planStructure}
            groupBy={(option) => option.blockName}
            options={sections}
            getOptionLabel={(option) => option.sectionName}
            renderGroup={(group) => (
              <Box key={group.key} sx={{ pb: 0.25 }}>
                <Stack
                  spacing={0.2}
                  sx={{
                    px: 0.5,
                    pt: 0.75,
                    position: "sticky",
                    top: 0,
                    backgroundColor: theme.palette.common.white,
                  }}
                >
                  <Typography variant="caption">{group.group.toLocaleUpperCase()}</Typography>
                  <Divider />
                </Stack>
                {group.children}
              </Box>
            )}
            renderOption={(props, option) => (
              <MenuItem {...props} key={option.sectionGuid} component="li">
                <Typography>{option.sectionName}</Typography>
              </MenuItem>
            )}
            isOptionEqualToValue={(option, value) => option.sectionGuid === value.sectionGuid}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select a strategic map section"
                inputProps={{ ...params.inputProps }}
              />
            )}
            onChange={(_, value) => setSelectedSection(value)}
            PopperComponent={(props) => (
              <Popper {...props} style={{ ...props.style, margin: 0, padding: 0, minWidth: "300px" }} />
            )}
            value={selectedSection}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" disabled={isUpdatingSection} onClick={handleCancelClicked}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={selectedSection == null || isUpdatingSection}
          endIcon={isUpdatingSection ? <CircularProgress size={20} sx={{ color: "grey.400" }} /> : null}
          onClick={handleDoneClicked}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { EditActionItemSectionModal };
export type { ITeamSection };
